import React, { useState, useEffect } from 'react';
import api from '../api';
import { Lightning, Clock, Calendar, User, Star } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

const GerenciarSolicitacoes = () => {
  const [solicitacoes, setSolicitacoes] = useState([]);
  const [clients, setClients] = useState([]);
  const [filterStatus, setFilterStatus] = useState('_all');
  const [loading, setLoading] = useState(true);
  
  // Assignment dialog
  const [isAssignOpen, setIsAssignOpen] = useState(false);
  const [selectedSolicitacao, setSelectedSolicitacao] = useState(null);
  const [compatiblePrestadores, setCompatiblePrestadores] = useState([]);
  const [nivelRequerido, setNivelRequerido] = useState(1);
  const [selectedPrestadorId, setSelectedPrestadorId] = useState('');
  const [loadingPrestadores, setLoadingPrestadores] = useState(false);

  useEffect(() => {
    fetchData();
  }, [filterStatus]);

  const fetchData = async () => {
    try {
      const params = {};
      if (filterStatus && filterStatus !== '_all') params.status = filterStatus;
      
      const [solRes, clientsRes] = await Promise.all([
        api.get('/solicitacoes', { params }),
        api.get('/clients')
      ]);
      setSolicitacoes(solRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client ? client.name : 'N/A';
  };

  const openAssignDialog = async (solicitacao) => {
    setSelectedSolicitacao(solicitacao);
    setSelectedPrestadorId('');
    setIsAssignOpen(true);
    setLoadingPrestadores(true);
    
    try {
      const res = await api.get(`/solicitacoes/${solicitacao.id}/prestadores-compativeis`);
      setCompatiblePrestadores(res.data.prestadores);
      setNivelRequerido(res.data.nivel_requerido);
    } catch (error) {
      console.error('Error fetching compatible prestadores:', error);
      // Fallback: get all active prestadores
      try {
        const res = await api.get('/prestadores?status=ativo');
        setCompatiblePrestadores(res.data);
        setNivelRequerido(1);
      } catch (e) {
        setCompatiblePrestadores([]);
      }
    } finally {
      setLoadingPrestadores(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedSolicitacao || !selectedPrestadorId) return;
    
    try {
      await api.post(`/solicitacoes/${selectedSolicitacao.id}/atribuir?prestador_id=${selectedPrestadorId}`);
      setIsAssignOpen(false);
      setSelectedSolicitacao(null);
      setSelectedPrestadorId('');
      fetchData();
    } catch (error) {
      alert('Erro ao atribuir: ' + (error.response?.data?.detail || error.message));
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'pendente': 'bg-yellow-500 text-black',
      'atribuida': 'bg-blue-500',
      'em_andamento': 'bg-purple-500',
      'aguardando_aprovacao': 'bg-orange-500',
      'concluida': 'bg-emerald-600',
      'cancelada': 'bg-red-500'
    };
    return colors[status] || 'bg-zinc-500';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'pendente': 'Pendente',
      'atribuida': 'Atribuida',
      'em_andamento': 'Em Andamento',
      'aguardando_aprovacao': 'Aguardando Aprovacao',
      'concluida': 'Concluida',
      'cancelada': 'Cancelada'
    };
    return labels[status] || status;
  };

  const getUrgenciaIcon = (urgencia) => {
    switch(urgencia) {
      case 'urgente': return <Lightning size={16} weight="fill" className="text-red-500" />;
      case 'rapido': return <Clock size={16} className="text-orange-500" />;
      case 'programado': return <Calendar size={16} className="text-blue-500" />;
      default: return <Clock size={16} className="text-zinc-400" />;
    }
  };

  const getTempoEspera = (createdAt) => {
    const now = new Date();
    const created = new Date(createdAt);
    const diffMs = now - created;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffDays > 0) return `${diffDays}d ${diffHours % 24}h`;
    if (diffHours > 0) return `${diffHours}h`;
    const diffMin = Math.floor(diffMs / (1000 * 60));
    return `${diffMin}min`;
  };

  const StarDisplay = ({ value, size = 12 }) => (
    <div className="flex gap-0.5">
      {[1,2,3,4,5].map(star => (
        <Star key={star} size={size} weight={star <= value ? 'fill' : 'regular'} className={star <= value ? 'text-[#FFB800]' : 'text-zinc-700'} />
      ))}
    </div>
  );

  if (loading) {
    return <div className="text-center py-8 text-zinc-400">Carregando...</div>;
  }

  // Stats
  const pendentes = solicitacoes.filter(s => s.status === 'pendente');
  const atribuidas = solicitacoes.filter(s => s.status === 'atribuida' || s.status === 'em_andamento');
  const aguardando = solicitacoes.filter(s => s.status === 'aguardando_aprovacao');

  return (
    <div>
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-[#121212] border border-[#27272A] rounded-sm p-4" data-testid="stat-total-solicitacoes">
          <div className="text-zinc-400 text-sm">Total</div>
          <div className="text-3xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed' }}>{solicitacoes.length}</div>
        </div>
        <div className="bg-[#121212] border border-red-900/30 rounded-sm p-4" data-testid="stat-pendentes">
          <div className="text-zinc-400 text-sm">Aguardando Tecnico</div>
          <div className="text-3xl font-bold text-red-500" style={{ fontFamily: 'Barlow Condensed' }}>{pendentes.length}</div>
        </div>
        <div className="bg-[#121212] border border-blue-900/30 rounded-sm p-4" data-testid="stat-em-atendimento">
          <div className="text-zinc-400 text-sm">Em Atendimento</div>
          <div className="text-3xl font-bold text-blue-500" style={{ fontFamily: 'Barlow Condensed' }}>{atribuidas.length}</div>
        </div>
        <div className="bg-[#121212] border border-orange-900/30 rounded-sm p-4" data-testid="stat-aguardando-aprovacao">
          <div className="text-zinc-400 text-sm">Aguardando Aprovacao</div>
          <div className="text-3xl font-bold text-orange-500" style={{ fontFamily: 'Barlow Condensed' }}>{aguardando.length}</div>
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-3 mb-6">
        <Label>Status:</Label>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-56 bg-[#121212] border-[#27272A] text-white" data-testid="filter-solicitacoes-status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#121212] border-[#27272A] text-white">
            <SelectItem value="_all">Todos</SelectItem>
            <SelectItem value="pendente">Pendente</SelectItem>
            <SelectItem value="atribuida">Atribuida</SelectItem>
            <SelectItem value="em_andamento">Em Andamento</SelectItem>
            <SelectItem value="aguardando_aprovacao">Aguardando Aprovacao</SelectItem>
            <SelectItem value="concluida">Concluida</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Solicitacoes List */}
      <div className="space-y-4">
        {solicitacoes.length === 0 ? (
          <div className="bg-[#121212] border border-[#27272A] rounded-sm p-8 text-center text-zinc-400">
            Nenhuma solicitacao encontrada
          </div>
        ) : (
          solicitacoes.map(sol => (
            <div key={sol.id} className="bg-[#121212] border border-[#27272A] rounded-sm p-5" data-testid={`solicitacao-${sol.numero_solicitacao}`}>
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-3">
                  <div className="text-lg font-bold text-[#FFB800]" style={{ fontFamily: 'Barlow Condensed' }}>
                    {sol.numero_solicitacao}
                  </div>
                  <Badge className={getStatusColor(sol.status)}>
                    {getStatusLabel(sol.status)}
                  </Badge>
                  <div className="flex items-center gap-1">
                    {getUrgenciaIcon(sol.urgencia)}
                    <span className="text-xs text-zinc-400">{sol.urgencia}</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-zinc-500">Tempo de espera</div>
                  <div className={`text-sm font-bold ${
                    sol.status === 'pendente' ? 'text-red-400' : 'text-zinc-400'
                  }`} data-testid={`tempo-espera-${sol.numero_solicitacao}`}>
                    {getTempoEspera(sol.created_at)}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                <div>
                  <div className="text-zinc-500 text-xs">Cliente</div>
                  <div className="text-white text-sm">{getClientName(sol.client_id)}</div>
                </div>
                <div>
                  <div className="text-zinc-500 text-xs">Prestador</div>
                  <div className="text-white text-sm">{sol.prestador_nome || 'Nao atribuido'}</div>
                </div>
                <div>
                  <div className="text-zinc-500 text-xs">Data</div>
                  <div className="text-white text-sm">{new Date(sol.created_at).toLocaleDateString('pt-BR')}</div>
                </div>
              </div>

              <div className="mt-3">
                <div className="text-zinc-500 text-xs mb-1">Servicos</div>
                <div className="flex flex-wrap gap-2">
                  {sol.servicos_solicitados?.map((s, idx) => (
                    <span key={idx} className="text-xs bg-[#1E1E1E] px-2 py-1 rounded text-zinc-300">{s}</span>
                  ))}
                </div>
              </div>

              {sol.status === 'pendente' && (
                <div className="mt-4 pt-3 border-t border-[#27272A]">
                  <Button
                    onClick={() => openAssignDialog(sol)}
                    className="bg-[#FFB800] text-black hover:bg-[#E6A600]"
                    data-testid={`assign-btn-${sol.numero_solicitacao}`}
                  >
                    <User size={16} className="mr-2" />
                    Atribuir Prestador
                  </Button>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Assign Prestador Dialog */}
      <Dialog open={isAssignOpen} onOpenChange={setIsAssignOpen}>
        <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">Atribuir Prestador</DialogTitle>
          </DialogHeader>
          {selectedSolicitacao && (
            <div className="space-y-4">
              <div className="p-3 bg-[#050505] border border-[#27272A] rounded-sm">
                <div className="text-[#FFB800] font-semibold">{selectedSolicitacao.numero_solicitacao}</div>
                <div className="text-zinc-400 text-sm mt-1">{getClientName(selectedSolicitacao.client_id)}</div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {selectedSolicitacao.servicos_solicitados?.map((s, i) => (
                    <span key={i} className="text-xs bg-[#121212] px-2 py-1 rounded text-zinc-300">{s}</span>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-2 text-sm">
                <span className="text-zinc-400">Nivel tecnico minimo:</span>
                <StarDisplay value={nivelRequerido} size={16} />
                <span className="text-[#FFB800] font-semibold">{nivelRequerido} estrela(s)</span>
              </div>

              {loadingPrestadores ? (
                <div className="text-center py-4 text-zinc-400">Carregando prestadores compativeis...</div>
              ) : compatiblePrestadores.length === 0 ? (
                <div className="text-center py-4 text-red-400">
                  Nenhum prestador compativel encontrado (nivel minimo: {nivelRequerido} estrelas)
                </div>
              ) : (
                <div>
                  <Label>Selecione o Prestador</Label>
                  <Select value={selectedPrestadorId} onValueChange={setSelectedPrestadorId}>
                    <SelectTrigger className="bg-[#050505] border-[#27272A] text-white" data-testid="assign-prestador-select">
                      <SelectValue placeholder="Selecione..." />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border-[#27272A] text-white">
                      {compatiblePrestadores.map(p => (
                        <SelectItem key={p.id} value={p.id}>
                          <div className="flex items-center gap-2">
                            <span>{p.name}</span>
                            <StarDisplay value={p.nivel_tecnico || 1} size={10} />
                            <span className="text-zinc-500 text-xs">R${p.valor_hora}/h</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="flex gap-3">
                <Button onClick={() => setIsAssignOpen(false)} className="flex-1 bg-[#1E1E1E] hover:bg-[#27272A]">
                  Cancelar
                </Button>
                <Button
                  onClick={handleAssign}
                  disabled={!selectedPrestadorId}
                  className="flex-1 bg-[#FFB800] text-black hover:bg-[#E6A600] disabled:opacity-50"
                  data-testid="confirm-assign-button"
                >
                  Confirmar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GerenciarSolicitacoes;
